import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Card, CardBody, Col, Container, Row } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import Table from './RequisitionTable';
import Pagination from '../../CustomControls/Pagination';
import {
  getAllRequisitions,
  setOnSearchChange,
  setOnConfirmationModal,
  deleteRequisition
} from '../../../store/actions/Operation/requisition';
import LoadingSpinner from '../../CustomControls/Spinner';
import Notification from '../../CustomControls/Notification';
import Breadcrumbs from '../../CustomControls/Breadcrumb';
import { _create, _reportprint, _requisition, _delete } from '../../../constants/componentConstants';
import { hasPermissionToAction } from '../../../utils/accessUtil';
import { getFirstDateOfCurrentMonth, getLastDateOfCurrentMonth } from '../../../utils';
import SearchBar from '../../CustomControls/SearchBar';
import DatePicker from '../../CustomControls/CustomDatePicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import CustomConfirmation from '../../CustomControls/ConfirmationModal';

import './style.scss';

const Requisition = props => {
  const [searchText, setSearchText] = useState('');
  const [searchCustomer, setSearchCustomer] = useState('');
  const [searchDepartment, setSearchDepartment] = useState('');
  const [searchFromDate, setSearchFromDate] = useState(getFirstDateOfCurrentMonth());
  const [searchToDate, setSearchToDate] = useState(getLastDateOfCurrentMonth());
  const history = useHistory();
  const dispatch = useDispatch();
  const requisition = useSelector(state => state.requisition);
  const ui = useSelector(state => state.ui);
  const permissions = ui.layout.permissions;
  const pageSize = ui.layout.pageSize;
  const currentPageNumber = requisition.filterOptions.pageNumber;

  const notificationType = requisition.isError ? 'error' : 'success';

  const showSaveOption = hasPermissionToAction(permissions, _requisition, _create);
  const showPrintOption = hasPermissionToAction(permissions, _requisition, _reportprint);
  const showDeleteOption = hasPermissionToAction(permissions, _requisition, _delete);

  useEffect(() => {
    dispatch(getAllRequisitions(currentPageNumber, pageSize, requisition.filterOptions.filterBy, requisition.filterOptions.sortBy));
  }, [dispatch, currentPageNumber, pageSize]);

  const onPageChangeHandler = currentPage => {
    setLocalChange({
      ...requisition,
      data: { ...requisition.filterOptions, pageNumber: currentPage }
    });
  };

  const onAddNewHandler = () => {
    history.push('/operation/requisitions/requisition');
  };

  const onEditHandler = id => {
    history.push(`/operation/requisitions/requisition/${id}`);
  };

  const onRepeatHandler = id => {
    history.push(`/operation/requisitions/requisition/1/${id}`);
  };

  const onDispatchHandler = id => {
    history.push(`/operation/dispatches/dispatch/1/${id}`);
  };

  const onPrintHandler = id => {
    history.push(`/operation/requisitions/requisition-report/${id}`);
  };

  const breadCrumbPaths = [
    { id: 1, title: 'Home', path: '/' },
    { id: 2, title: 'Requisition Listing', path: '/operation/requisitions', isCurrent: true }
  ];

  useEffect(() => {
    return () => {
      let filterListCurrent = requisition.filterOptions.filterBy || [];
      if (filterListCurrent.length > 0) {
        filterListCurrent = filterListCurrent.filter(
          x =>
            x.name.toLowerCase() !== 'requisitionref' &&
            x.name.toLowerCase() !== 'clientprofilename' &&
            x.name.toLowerCase() !== 'departmentname'
        );
      }
      setLocalChange({
        ...requisition,
        data: { ...requisition.filterOptions, filterBy: filterListCurrent }
      });
    };
  }, []);

  const handleInput = e => {
    const searchVal = e.target.value ?? '';
    setSearchText(searchVal);
    let filterSearchList = [...(requisition.filterOptions.filterBy || [])];
    const objIndex = filterSearchList.findIndex(x => x.name.toLowerCase() === 'requisitionref');
    if (objIndex !== -1) {
      filterSearchList[objIndex] = { ...filterSearchList[objIndex], value: searchVal.toLowerCase() };
    } else {
      filterSearchList.push({ name: 'requisitionref', value: searchVal.toLowerCase() });
    }
    setLocalChange({
      ...requisition,
      data: { ...requisition.filterOptions, filterBy: filterSearchList }
    });
  };

  const handleCustomerInput = e => {
    const searchVal = e.target.value ?? '';
    setSearchCustomer(searchVal);
    let filterSearchList = [...(requisition.filterOptions.filterBy || [])];
    const objIndex = filterSearchList.findIndex(x => x.name.toLowerCase() === 'clientprofilename');
    if (objIndex !== -1) {
      filterSearchList[objIndex] = { ...filterSearchList[objIndex], value: searchVal.toLowerCase() };
    } else {
      filterSearchList.push({ name: 'clientprofilename', value: searchVal.toLowerCase() });
    }
    setLocalChange({
      ...requisition,
      data: { ...requisition.filterOptions, filterBy: filterSearchList }
    });
  };

  const handleDepartmentInput = e => {
    const searchVal = e.target.value ?? '';
    setSearchDepartment(searchVal);
    let filterSearchList = [...(requisition.filterOptions.filterBy || [])];
    const objIndex = filterSearchList.findIndex(x => x.name.toLowerCase() === 'departmentname');
    if (objIndex !== -1) {
      filterSearchList[objIndex] = { ...filterSearchList[objIndex], value: searchVal.toLowerCase() };
    } else {
      filterSearchList.push({ name: 'departmentname', value: searchVal.toLowerCase() });
    }
    setLocalChange({
      ...requisition,
      data: { ...requisition.filterOptions, filterBy: filterSearchList }
    });
  };
  const handleDateInput = (field, dateVal) => {
    const setDate = dateVal ? new Date(dateVal) : null;
    if (field === 'fromdate') {
      setSearchFromDate(setDate);
    }

    if (field === 'todate') {
      setSearchToDate(setDate);
    }

    let filterSearchList = requisition.filterOptions.filterBy || [];

    const objIndex = filterSearchList.findIndex(x => x.name.toLowerCase() === field);

    if (objIndex !== -1) {
      filterSearchList[objIndex].value = `${setDate.getFullYear()}-${String(setDate.getMonth() + 1).padStart(2, '0')}-${String(
        setDate.getDate()
      ).padStart(2, '0')}`;
    } else {
      filterSearchList.push({
        name: field,
        value: `${setDate.getFullYear()}-${String(setDate.getMonth() + 1).padStart(2, '0')}-${String(setDate.getDate()).padStart(2, '0')}`
      });
    }

    setLocalChange({
      ...requisition,
      data: { ...requisition.filterOptions, filterBy: filterSearchList }
    });
  };

  const setLocalChange = currentState => {
    dispatch(setOnSearchChange(currentState));
  };

  const onSearchHandler = e => {
    dispatch(getAllRequisitions(currentPageNumber, pageSize, requisition.filterOptions.filterBy, requisition.filterOptions.sortBy));
  };

  const onDeleteHandler = id => {
    dispatch(
      setOnConfirmationModal({
        data: {
          showConfirmationModal: true,
          confirmationId: id
        }
      })
    );
  };

  const onDeleteConfirmation = () => {
    if (requisition.confirmationId) {
      let ftOptions = {
        currentPageNumber: currentPageNumber,
        pageSize: pageSize,
        filterBy: requisition.filterOptions.filterBy,
        sortBy: requisition.filterOptions.sortBy
      };
      dispatch(deleteRequisition(requisition.confirmationId, ftOptions));
    }
  };

  const onDeleteCancel = () => {
    dispatch(
      setOnConfirmationModal({
        data: {
          showConfirmationModal: false,
          confirmationId: ''
        }
      })
    );
  };

  return (
    <React.Fragment>
      <CustomConfirmation
        show={requisition.showConfirmationModal}
        onDeleteClick={onDeleteConfirmation}
        onCloseClick={onDeleteCancel}
        message="Are you sure you want to delete this record?"
        header="Deleting record"
      />
      <div className="page-content">
        {requisition.showMessage && <Notification notificationType={notificationType} message={requisition.message} />}
        <Container fluid>
          <Breadcrumbs title="Requisition Listing " paths={breadCrumbPaths} />
          <Row>
            <Col xs="12">
              <Card>
                <CardBody>
                  <Row className="mb-2 buttonrow">
                    <Col sm="2">
                      <SearchBar onChange={handleInput} placeholder="Requisition No" value={searchText} />
                    </Col>
                    <Col sm="3">
                      <SearchBar onChange={handleCustomerInput} placeholder="Search By Customer" value={searchCustomer} />
                    </Col>
                    <Col sm="3">
                      <SearchBar onChange={handleDepartmentInput} placeholder="Search By Department" value={searchDepartment} />
                    </Col>
                    <Col sm="4">
                      <div className="text-sm-end">
                        {showSaveOption && (
                          <Button type="button" className="btn btn-primary btn-block app-add-button" onClick={onAddNewHandler}>
                            NEW REQUISITION
                          </Button>
                        )}
                      </div>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col sm="2">
                      <div className="mb-4">
                        <DatePicker
                          label="From Date"
                          selected={searchFromDate}
                          onChange={date => handleDateInput('fromdate', date)}
                          dateFormat="dd-MMM-yyyy"
                        />
                      </div>
                    </Col>
                    <Col sm="2">
                      <div className="mb-4">
                        <DatePicker
                          label="To Date"
                          selected={searchToDate}
                          onChange={date => handleDateInput('todate', date)}
                          dateFormat="dd-MMM-yyyy"
                          minDate={searchFromDate}
                        />
                      </div>
                    </Col>
                    <Col sm="2" className="m-auto">
                      <div className="text-sm-start">
                        <Button type="button" className="btn btn-outline-primary" onClick={onSearchHandler}>
                          <FontAwesomeIcon className="standardIcon" icon={faSearch} /> Search
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <Row className="tablerow">
                    <Col xl="12">
                      <div>
                        {requisition.isLoading && <LoadingSpinner />}
                        <Pagination
                          data={requisition.requisitionList}
                          tZone={requisition.tZone}
                          pageSize={pageSize}
                          onPageChange={onPageChangeHandler}
                          startingPage={currentPageNumber}
                          apiPaging={true}
                          apiDataLength={requisition.totalRecords}
                        >
                          <Table
                            editHandler={onEditHandler}
                            repeatHandler={onRepeatHandler}
                            dispatchHandler={onDispatchHandler}
                            printHandler={onPrintHandler}
                            showPrint={showPrintOption}
                            deleteHandler={onDeleteHandler}
                            showDelete={showDeleteOption}
                          />
                        </Pagination>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Requisition;
